import { FC, ReactNode } from "react";
import { Button } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { translate } from "../../../core";
import styles from "./Order.module.scss";

interface OrderFormLinesSortableProps {
  ids: string[];
  onMove: (from: number, to: number) => void;
  children: ReactNode;
}

export const OrderFormLinesSortable: FC<OrderFormLinesSortableProps> = ({
  ids,
  onMove,
  children,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // A field that still has focus would keep taking keystrokes while its row is being moved.
  const onDragStart = ({ activatorEvent }: DragStartEvent) => {
    const focused = document.activeElement;

    if (focused instanceof HTMLElement && !focused.contains(activatorEvent.target as Node))
      focused.blur();
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));

    if (from < 0 || to < 0) return;

    onMove(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

interface OrderFormLineRowProps {
  id: string;
  disabled?: boolean;
  children: ReactNode;
}

// The row is full of inputs, so the sortable role, tab stop and listeners sit on the handle
// instead of turning the whole <tr> into a focusable button.
export const OrderFormLineRow: FC<OrderFormLineRowProps> = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  return (
    <tr
      ref={setNodeRef}
      className={isDragging ? styles.orderLineDragging : undefined}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <td className={styles.orderLineDragCell}>
        <Button
          type="text"
          icon={<HolderOutlined />}
          className={styles.orderLineDragHandle}
          disabled={disabled}
          title={translate("drag_to_reorder")}
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
        />
      </td>
      {children}
    </tr>
  );
};
